import { module } from "angular";
import { cloneDeep, forEach, map as _map, orderBy } from "lodash";
import { map } from "rxjs/operators";

import type { Observable } from "rxjs";

import { DirectConnectionType } from "App/Modules/Models/Models/DirectConnectionType";
import type { IAvailableChannel } from "App/Modules/Models/Models/IAvailableChannel";
import type { IDirectConnection } from "App/Modules/Models/Models/IDirectConnection";
import type { IHttpService } from "App/Modules/Services";
import type {
    IEquipmentDetails,
    IResponse,
    ISiteEquipmentDetails,
    ISiteEquipmentNormalized,
    ISiteEquipmentSlim,
    ISiteEquipmentWithCoverages,
    IVideoStream,
    IViewCoverage,
} from "../../Models";
import {
    CommunicationInterfaceType,
    EquipmentClass,
    EquipmentClassFunctions,
    EquipmentFunctions,
    EquipmentTypeFunctions,
    VideoStreamFunctions,
    ViewCoverageFunctions,
} from "../../Models";
import type { ISiteEquipmentRepository } from "./ISiteEquipmentRepository";

export default class SiteEquipmentRepository implements ISiteEquipmentRepository {
    public static $inject: string[] = ["mmHttpService"];

    private static enhanceSiteEquipmentList<T extends ISiteEquipmentNormalized[]>(sel: T) {
        sel.forEach((se) => this.enhanceSiteEquipment(se));
    }

    private static enhanceSiteEquipment<T extends ISiteEquipmentNormalized>(se: T) {
        se.className = se.class as any;
        se.class = EquipmentClass[se.className];
        se.icon = EquipmentClassFunctions.EquipmentClassIcon(se.class);
    }

    private static transformSiteEquipment(se: ISiteEquipmentWithCoverages) {
        se.className = se.class as any;
        se.class = EquipmentClass[se.className];
        se.icon = EquipmentClassFunctions.EquipmentClassIcon(se.class);
        if (se.viewCoverages) {
            se.viewCoverages.forEach((vc) => {
                ViewCoverageFunctions.enhanceViewCoverage(vc);
                vc.minDownwardTilt = 1;
            });
        }

        if (se.equipment) {
            EquipmentFunctions.enhanceEquipment(se.equipment);
        }

        if (se.videoStreams !== null && se.videoStreams !== undefined && se.videoStreams.length > 0) {
            VideoStreamFunctions.enhanceVideoStreams(se.videoStreams);
        }

        if (se.type) {
            EquipmentTypeFunctions.enhanceEquipmentType(se.type);
        }
    }

    private static transformDirectConnection(dc: IDirectConnection) {
        dc.directConnectionTypeName = dc.directConnectionType as any;
        dc.directConnectionType = DirectConnectionType[dc.directConnectionTypeName];

        if (dc.inputSiteEquipment) {
            this.transformSiteEquipment(dc.inputSiteEquipment as ISiteEquipmentWithCoverages);
        }

        if (dc.outputSiteEquipment) {
            this.transformSiteEquipment(dc.outputSiteEquipment as ISiteEquipmentWithCoverages);
        }

        if (dc.inputChannel) {
            // todo extract maping to function

            dc.inputChannel.supportedDirectConnections = _map(
                dc.inputChannel.supportedDirectConnections,
                (connection) => DirectConnectionType[connection as unknown as keyof typeof DirectConnectionType],
            );
        }

        if (dc.outputChannel) {
            dc.outputChannel.supportedDirectConnections = _map(
                dc.outputChannel.supportedDirectConnections,
                (connection) => DirectConnectionType[connection as unknown as keyof typeof DirectConnectionType],
            );
        }
    }

    constructor(private readonly httpService: IHttpService) {}

    public getForSite(siteId: string): Observable<IResponse<ISiteEquipmentNormalized[]>> {
        return this.httpService.get<ISiteEquipmentNormalized[]>(`sites/${siteId}/equipment/normalized`).pipe(
            map((response) => {
                SiteEquipmentRepository.enhanceSiteEquipmentList(response.data);
                return response;
            }),
        );
    }

    public getForSiteSlim(siteId: string): Observable<IResponse<ISiteEquipmentSlim[]>> {
        return this.httpService.get(`sites/${siteId}/equipment`);
    }

    public getSiteEquipment(siteId: string, siteEquipmentId: string): Observable<IResponse<ISiteEquipmentDetails>> {
        return this.httpService.get(`sites/${siteId}/equipment/${siteEquipmentId}`);
    }

    public getSiteEquipmentWithCoverages(
        siteId: string,
        siteEquipmentId: string,
    ): Observable<IResponse<ISiteEquipmentWithCoverages>> {
        return this.httpService
            .get<ISiteEquipmentWithCoverages>(`sites/${siteId}/equipment/${siteEquipmentId}/withCoverages`)
            .pipe(
                map((response) => {
                    if (!response.data) {
                        return response;
                    }

                    SiteEquipmentRepository.transformSiteEquipment(response.data);

                    return response;
                }),
            );
    }

    public requestTestOfSiteEquipment(siteId: string, siteEquipmentId: string): Observable<IResponse<void>> {
        return this.httpService.post<void>(`sites/${siteId}/equipment/${siteEquipmentId}/requestTest`);
    }

    public getDirectConnections(siteId: string, siteEquipmentId: string): Observable<IResponse<IDirectConnection[]>> {
        return this.httpService
            .get<IDirectConnection[]>(`sites/${siteId}/equipment/${siteEquipmentId}/directConnections`)
            .pipe(
                map((response) => {
                    if (!response.data) {
                        return response;
                    }

                    forEach(response.data, (dc: IDirectConnection) =>
                        SiteEquipmentRepository.transformDirectConnection(dc),
                    );

                    return response;
                }),
            );
    }

    public getAvailableChannels(siteId: string, siteEquipmentId: string): Observable<IResponse<IAvailableChannel[]>> {
        return this.httpService
            .get<IAvailableChannel[]>(`sites/${siteId}/equipment/${siteEquipmentId}/availableChannels`)
            .pipe(
                map((response: IResponse<IAvailableChannel[]>) => {
                    if (!response.data) {
                        return response;
                    }

                    forEach(response.data, (channel: IAvailableChannel) => {
                        channel.supportedDirectConnections = _map(
                            channel.supportedDirectConnections,
                            (connection) =>
                                DirectConnectionType[connection as unknown as keyof typeof DirectConnectionType],
                        );
                    });

                    response.data = orderBy(response.data, "name");

                    return response;
                }),
            );
    }

    public addSiteEquipment(
        siteId: string,
        siteEquipment: ISiteEquipmentSlim,
    ): Observable<IResponse<ISiteEquipmentSlim>> {
        return this.httpService.post<ISiteEquipmentSlim>(`sites/${siteId}/equipment`, siteEquipment).pipe(
            map((response: IResponse<ISiteEquipmentSlim>) => {
                if (!response.data) {
                    return response;
                }

                SiteEquipmentRepository.transformSiteEquipment(response.data as ISiteEquipmentWithCoverages);

                return response;
            }),
        );
    }

    public addViewCoverage(
        siteId: string,
        siteEquipmentId: string,
        viewCoverage: IViewCoverage,
    ): Observable<IResponse<IViewCoverage>> {
        return this.httpService
            .post<IViewCoverage>(`sites/${siteId}/equipment/${siteEquipmentId}/coverages`, viewCoverage)
            .pipe(
                map((response: IResponse<IViewCoverage>) => {
                    if (!response.data) {
                        return response;
                    }

                    ViewCoverageFunctions.enhanceViewCoverage(response.data);

                    return response;
                }),
            );
    }

    public addVideoStream(siteId: string, siteEquipmentId: string): Observable<IResponse<IVideoStream>> {
        return this.httpService.post<null>(`sites/${siteId}/equipment/${siteEquipmentId}/videoStreams`).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                VideoStreamFunctions.enhanceVideoStream(response.data);

                return response;
            }),
        );
    }

    public createDirectConnection(
        siteId: string,
        directConnection: IDirectConnection,
    ): Observable<IResponse<IDirectConnection>> {
        directConnection.directConnectionType = DirectConnectionType[directConnection.directConnectionType] as any;

        return this.httpService
            .post<IDirectConnection>(`sites/${siteId}/equipment/createConnection`, directConnection)
            .pipe(
                map((response: IResponse<IDirectConnection>) => {
                    SiteEquipmentRepository.transformDirectConnection(response.data);

                    return response;
                }),
            );
    }

    public patch(
        siteId: string,
        siteEquipmentId: string,
        siteEquipment: ISiteEquipmentWithCoverages,
    ): Observable<IResponse<ISiteEquipmentWithCoverages>> {
        return this.httpService
            .patch<ISiteEquipmentWithCoverages>(`sites/${siteId}/equipment/${siteEquipmentId}`, siteEquipment)
            .pipe(
                map((response: IResponse<ISiteEquipmentWithCoverages>) => {
                    if (!response.data) {
                        return response;
                    }

                    SiteEquipmentRepository.transformSiteEquipment(response.data);

                    if (response.data.viewCoverages && response.data.viewCoverages.length > 0) {
                        forEach(response.data.viewCoverages, (vc) => {
                            ViewCoverageFunctions.enhanceViewCoverage(vc);
                        });
                    }

                    return response;
                }),
            );
    }

    public patchEquipment(
        siteId: string,
        siteEquipmentId: string,
        equipment: IEquipmentDetails,
    ): Observable<IResponse<IEquipmentDetails>> {
        const eqClone = cloneDeep(equipment);

        if (equipment.communicationConfigurations) {
            eqClone.communicationConfigurations.forEach((v) => {
                v.type = CommunicationInterfaceType[v.type] as any;
            });
        }

        return this.httpService
            .patch<IEquipmentDetails>(`sites/${siteId}/equipment/${siteEquipmentId}/equipment`, eqClone)
            .pipe(
                map((response: IResponse<IEquipmentDetails>) => {
                    if (!response.data) {
                        return response;
                    }

                    EquipmentFunctions.enhanceEquipment(response.data);

                    return response;
                }),
            );
    }

    public patchViewCoverage(
        siteId: string,
        siteEquipmentId: string,
        viewCoverageId: string,
        viewCoverage: IViewCoverage,
    ): Observable<IResponse<IViewCoverage>> {
        return this.httpService
            .patch<IViewCoverage>(
                `sites/${siteId}/equipment/${siteEquipmentId}/coverages/${viewCoverageId}`,
                viewCoverage,
            )
            .pipe(
                map((response: IResponse<IViewCoverage>) => {
                    if (!response.data) {
                        return response;
                    }

                    ViewCoverageFunctions.enhanceViewCoverage(response.data);

                    return response;
                }),
            );
    }

    public delete(siteId: string, siteEquipmentId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`sites/${siteId}/equipment/${siteEquipmentId}`);
    }

    public deleteViewCoverage(
        siteId: string,
        siteEquipmentId: string,
        viewCoverageId: string,
    ): Observable<IResponse<void>> {
        return this.httpService.delete(`sites/${siteId}/equipment/${siteEquipmentId}/coverages/${viewCoverageId}`);
    }

    public disconnectEquipment(
        siteId: string,
        siteEquipmentId: string,
    ): Observable<IResponse<ISiteEquipmentWithCoverages>> {
        return this.httpService
            .put<ISiteEquipmentWithCoverages>(`sites/${siteId}/equipment/${siteEquipmentId}/disconnect`, null)
            .pipe(
                map((response: IResponse<ISiteEquipmentWithCoverages>): IResponse<ISiteEquipmentWithCoverages> => {
                    if (!response.data) {
                        return response;
                    }

                    SiteEquipmentRepository.transformSiteEquipment(response.data);

                    return response;
                }),
            );
    }

    public deleteDirectConnection(siteId: string, directConnectionId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`sites/${siteId}/equipment/deleteConnection/${directConnectionId}`);
    }

    public getVideoStreamsForSiteEquipment(
        siteId: string,
        siteEquipmentId: string,
    ): Observable<IResponse<IVideoStream[]>> {
        return this.httpService.get(`sites/${siteId}/equipment/${siteEquipmentId}/videoStreams`);
    }

    public isInUseAsMasterSiteEquipment(siteId: string, siteEquipmentId: string): Observable<IResponse<boolean>> {
        return this.httpService.get(`sites/${siteId}/equipment/${siteEquipmentId}/isInUseAsMasterSiteEquipment`);
    }
}

module("mmRepositories").service("mmSiteEquipmentRepository", SiteEquipmentRepository);
